import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { PartyNationalResult } from '@/types'

interface VoteShareChartProps {
  partyResults: PartyNationalResult[]
  thresholdPercent: number
  totalVotes: number
  className?: string
}

export function VoteShareChart({
  partyResults,
  thresholdPercent,
  totalVotes,
  className,
}: VoteShareChartProps) {
  const chartData = useMemo(
    () =>
      [...partyResults].sort((a, b) => b.vote_share - a.vote_share),
    [partyResults]
  )

  const partiesBelowThreshold = chartData.filter((p) => p.vote_share < thresholdPercent)

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Vote Share</CardTitle>
        <CardDescription>
          {totalVotes.toLocaleString()} total votes · {thresholdPercent}% electoral threshold
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="party_abbreviation"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                domain={[0, 'auto']}
                tick={{ fontSize: 12 }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload as PartyNationalResult
                    const belowThreshold = item.vote_share < thresholdPercent
                    return (
                      <div className="bg-popover text-popover-foreground rounded-lg border p-2 shadow-md">
                        <div className="font-medium">{item.party_name}</div>
                        <div className="text-muted-foreground text-sm">
                          {item.vote_share.toFixed(2)}% ({item.total_votes.toLocaleString()} votes)
                        </div>
                        {belowThreshold && (
                          <div className="text-red-500 text-xs mt-1">
                            Below {thresholdPercent}% threshold — no seats
                          </div>
                        )}
                      </div>
                    )
                  }
                  return null
                }}
              />
              {thresholdPercent > 0 && (
                <ReferenceLine
                  y={thresholdPercent}
                  stroke="#f59e0b"
                  strokeDasharray="3 3"
                  label={{ value: `${thresholdPercent}%`, position: 'right', fontSize: 11, fill: '#f59e0b' }}
                />
              )}
              <Bar dataKey="vote_share" radius={[4, 4, 0, 0]}>
                {chartData.map((entry) => (
                  <Cell
                    key={entry.party_id}
                    fill={entry.party_color}
                    opacity={entry.vote_share < thresholdPercent ? 0.4 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {partiesBelowThreshold.length > 0 && (
          <p className="text-muted-foreground text-xs mt-3">
            {partiesBelowThreshold.map((p) => p.party_name).join(', ')}
            {' '}fell below the {thresholdPercent}% threshold and received no seats.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
